<template>
  <div class="energy-flow-page">
    <div class="flow-layout">
      <aside class="tree-panel">
        <div class="tree-title">空间维度</div>
        <el-input
          v-model="treeFilter"
          size="small"
          placeholder="搜索"
          prefix-icon="el-icon-search"
          clearable
          class="tree-search"
        />
        <el-tree
          ref="spaceTree"
          :data="spaceTree"
          show-checkbox
          node-key="id"
          default-expand-all
          :filter-node-method="filterNode"
          :props="{ label: 'label', children: 'children' }"
          @check="handleTreeCheck"
        />
      </aside>

      <div class="flow-main">
        <filter-bar>
          <div class="filter-item">
            <span class="filter-label">能源类型</span>
            <el-button
              v-for="item in energyTypes"
              :key="item.key"
              :type="item.active ? 'primary' : 'default'"
              size="mini"
              @click="selectEnergyType(item.key)"
            >
              {{ item.label }}
            </el-button>
          </div>
          <div class="filter-item">
            <span class="filter-label">时间周期</span>
            <el-radio-group v-model="period" size="small">
              <el-radio-button label="day">日</el-radio-button>
              <el-radio-button label="month">月</el-radio-button>
              <el-radio-button label="year">年</el-radio-button>
            </el-radio-group>
          </div>
          <div class="filter-item">
            <span class="filter-label">时间段</span>
            <el-date-picker
              v-model="dateRange"
              type="daterange"
              size="small"
              range-separator="至"
              start-placeholder="开始"
              end-placeholder="结束"
              value-format="yyyy-MM-dd"
            />
          </div>
          <div class="filter-item" style="margin-left: auto">
            <el-button type="primary" size="small" @click="loadData">查询</el-button>
          </div>
        </filter-bar>

        <div class="summary-row">
          <div class="summary-card">
            <div class="summary-label">能源输入总量</div>
            <div class="summary-value">
              {{ formatNumber(summary.totalInput, 0) }}
              <span class="unit">kWh</span>
            </div>
          </div>
          <div class="summary-card">
            <div class="summary-label">终端消耗总量</div>
            <div class="summary-value">
              {{ formatNumber(summary.totalConsumption, 0) }}
              <span class="unit">kWh</span>
            </div>
          </div>
          <div class="summary-card">
            <div class="summary-label">损耗量 / 损耗率</div>
            <div class="summary-value">
              {{ formatNumber(summary.totalLoss, 0) }}
              <span class="unit">kWh</span>
              <span class="sub-rate">/ {{ summary.lossRate }}%</span>
            </div>
          </div>
          <div class="summary-card" :class="{ highlight: summary.abnormalLevels > 0 }">
            <div class="summary-label">异常层级数</div>
            <div class="summary-value">
              {{ summary.abnormalLevels }}
              <span class="unit">层</span>
              <span class="sub-rate">阈值 {{ summary.threshold }}%</span>
            </div>
          </div>
        </div>

        <div class="balance-table-wrap">
          <div class="balance-table-header">
            <span class="balance-table-title">逐层平衡摘要</span>
            <el-button type="text" size="small" @click="goToBalance">查看校验明细</el-button>
          </div>
          <table class="balance-table">
            <thead>
              <tr>
                <th v-for="col in balanceTable.columns" :key="col">{{ col }}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td class="row-label">逐层平衡差值 (kWh)</td>
                <td v-for="(val, i) in balanceTable.differences" :key="'d-' + i">{{ val }}</td>
              </tr>
              <tr>
                <td class="row-label">不平衡率</td>
                <td
                  v-for="(val, i) in balanceTable.imbalanceRates"
                  :key="'r-' + i"
                  :class="{ 'text-danger': val > summary.threshold }"
                >
                  {{ val }}%
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <chart-card title="能流桑基图" class="sankey-wrap">
          <template slot="extra">
            <span v-if="scopeLabel" class="scope-label">{{ scopeLabel }}</span>
            <el-button type="text" size="small" @click="sankeyExpanded = !sankeyExpanded">
              {{ sankeyExpanded ? '收起' : '展开全部' }}
            </el-button>
          </template>
          <base-chart :option="sankeyOption" />
        </chart-card>
      </div>
    </div>
  </div>
</template>

<script>
import FilterBar from '@/components/common/FilterBar.vue'
import ChartCard from '@/components/common/ChartCard.vue'
import BaseChart from '@/components/charts/BaseChart.vue'
import { getAnalysisData } from '@/api/modules/energyFlow'
import { formatNumber } from '@/utils/format'

const SANKEY_COLORS = ['#f06292', '#ff9800', '#8bc34a', '#2196f3', '#9c27b0', '#c62828']

export default {
  name: 'EnergyFlowAnalysis',
  components: { FilterBar, ChartCard, BaseChart },
  data() {
    return {
      treeFilter: '',
      spaceTree: [],
      checkedSpaceIds: [],
      energyTypes: [],
      period: 'day',
      dateRange: ['2025-07-01', '2025-07-07'],
      summary: {
        totalInput: 0,
        totalConsumption: 0,
        totalLoss: 0,
        lossRate: 0,
        abnormalLevels: 0,
        threshold: 10
      },
      balanceTable: { columns: [], differences: [], imbalanceRates: [] },
      sankeyOption: {},
      sankeyData: null,
      sankeyExpanded: true,
      pendingEnergyType: null,
      scopeLabel: ''
    }
  },
  watch: {
    treeFilter(val) {
      this.$refs.spaceTree.filter(val)
    },
    sankeyExpanded() {
      if (this.sankeyData) {
        this.buildSankeyChart(this.sankeyData)
      }
    }
  },
  created() {
    this.applyRouteQuery()
    this.loadData()
  },
  methods: {
    formatNumber,
    applyRouteQuery() {
      const { energyType, period, startDate, endDate, spaceIds } = this.$route.query
      if (period) this.period = period
      if (startDate && endDate) this.dateRange = [startDate, endDate]
      if (spaceIds) {
        this.checkedSpaceIds = String(spaceIds).split(',')
      }
      if (energyType) {
        this.pendingEnergyType = energyType
      }
    },
    getActiveEnergyType() {
      const active = this.energyTypes.find(item => item.active)
      return active ? active.key : (this.pendingEnergyType || 'electricity')
    },
    buildQueryParams() {
      const checkedKeys = this.$refs.spaceTree
        ? this.$refs.spaceTree.getCheckedKeys()
        : this.checkedSpaceIds
      return {
        spaceIds: checkedKeys.length ? checkedKeys : undefined,
        energyType: this.getActiveEnergyType(),
        period: this.period,
        startDate: this.dateRange && this.dateRange[0],
        endDate: this.dateRange && this.dateRange[1],
        threshold: this.summary.threshold
      }
    },
    async loadData() {
      const params = this.buildQueryParams()
      const res = await getAnalysisData(params)
      const data = res.data
      this.spaceTree = data.spaceTree
      this.energyTypes = data.energyTypes
      if (this.pendingEnergyType) {
        this.selectEnergyType(this.pendingEnergyType)
        this.pendingEnergyType = null
      }
      this.summary = data.summary
      this.balanceTable = data.balanceTable
      this.sankeyData = data.sankey
      this.scopeLabel = data.summary.selectedRoots && data.summary.selectedRoots.length
        ? `当前范围：${data.summary.selectedRoots.join('、')}`
        : ''
      this.buildSankeyChart(data.sankey)
      this.$nextTick(() => {
        if (this.checkedSpaceIds.length && this.$refs.spaceTree) {
          this.$refs.spaceTree.setCheckedKeys(this.checkedSpaceIds)
          this.checkedSpaceIds = []
        }
      })
    },
    filterNode(value, data) {
      if (!value) return true
      return data.label.indexOf(value) !== -1
    },
    handleTreeCheck() {
      this.checkedSpaceIds = []
    },
    selectEnergyType(key) {
      this.energyTypes = this.energyTypes.map(item => ({
        ...item,
        active: item.key === key
      }))
    },
    buildSankeyChart(sankey) {
      const nodes = sankey.nodes.map((node, index) => ({
        ...node,
        itemStyle: { color: SANKEY_COLORS[index % SANKEY_COLORS.length] }
      }))
      const rootNames = this.getSankeyRootNames(sankey.links)
      const links = this.sankeyExpanded
        ? sankey.links
        : sankey.links.filter(link => rootNames.has(link.source))
      this.sankeyOption = {
        tooltip: {
          trigger: 'item',
          formatter: params => {
            if (params.dataType === 'edge') {
              return `${params.data.source} → ${params.data.target}<br/>流量: ${params.data.value} kWh`
            }
            return params.name
          }
        },
        series: [{
          type: 'sankey',
          layout: 'none',
          emphasis: { focus: 'adjacency' },
          nodeAlign: 'left',
          nodeGap: 14,
          layoutIterations: 48,
          data: nodes,
          links,
          lineStyle: { color: 'source', curveness: 0.72, opacity: 0.45 },
          label: { fontSize: 11 }
        }]
      }
    },
    getSankeyRootNames(links) {
      const targets = new Set(links.map(link => link.target))
      const roots = links.filter(link => !targets.has(link.source)).map(link => link.source)
      return new Set(roots.length ? roots : links.map(link => link.source).slice(0, 1))
    },
    goToBalance() {
      const params = this.buildQueryParams()
      this.$router.push({
        path: '/energy-flow/balance',
        query: {
          energyType: params.energyType,
          period: params.period,
          startDate: params.startDate,
          endDate: params.endDate,
          spaceId: params.spaceIds && params.spaceIds[0],
          threshold: this.summary.threshold
        }
      })
    }
  }
}
</script>

<style lang="less" scoped>
@import '~@/styles/variables.less';

.flow-layout {
  display: flex;
  gap: 16px;
  height: calc(100vh - @header-height - 32px);
}

.tree-panel {
  width: 240px;
  flex-shrink: 0;
  background: @card-bg;
  border-radius: @card-radius;
  box-shadow: @card-shadow;
  padding: 12px;
  overflow-y: auto;

  .tree-title {
    font-size: 14px;
    font-weight: 600;
    margin-bottom: 10px;
  }

  .tree-search {
    margin-bottom: 10px;
  }
}

.flow-main {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.summary-row {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  margin-bottom: 16px;
}

.summary-card {
  background: @card-bg;
  border-radius: @card-radius;
  box-shadow: @card-shadow;
  padding: 12px 16px;
  background: linear-gradient(135deg, #fff 60%, rgba(64, 158, 255, 0.06));

  &.highlight {
    background: linear-gradient(135deg, #fff 60%, rgba(245, 108, 108, 0.08));
  }

  .summary-label {
    font-size: 13px;
    color: @text-secondary;
    margin-bottom: 6px;
  }

  .summary-value {
    font-size: 20px;
    font-weight: 700;
    color: @text-primary;

    .unit {
      font-size: 12px;
      font-weight: 400;
      color: @text-secondary;
      margin-left: 2px;
    }

    .sub-rate {
      font-size: 12px;
      font-weight: 400;
      color: @text-secondary;
      margin-left: 6px;
    }
  }
}

.balance-table-wrap {
  background: @card-bg;
  border-radius: @card-radius;
  box-shadow: @card-shadow;
  padding: 12px;
  margin-bottom: 16px;
  overflow-x: auto;
}

.balance-table-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;

  .balance-table-title {
    font-size: 14px;
    font-weight: 600;
    color: @text-primary;
  }
}

.balance-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;

  th, td {
    border: 1px solid @border-color;
    padding: 8px 12px;
    text-align: center;
  }

  th {
    background: #fafafa;
    font-weight: 600;
  }

  .row-label {
    text-align: left;
    font-weight: 600;
    white-space: nowrap;
  }
}

.sankey-wrap {
  flex: 1;
  min-height: 400px;

  .scope-label {
    font-size: 12px;
    color: @text-secondary;
    margin-right: 8px;
  }
}
</style>
